<template>
  <div>
    <b-card>
      <b-card-title>
        <div class="d-flex justify-content-between align-items-center">
          <span>المعلومات الشخصية</span>
          <b-button
            v-if="!isEditing"
            variant="primary"
            size="sm"
            @click="startEditing"
          >
            <feather-icon
              icon="EditIcon"
              size="14"
              class="mr-50"
            />
            تعديل الملف الشخصي
          </b-button>
          <div v-else>
            <b-button
              variant="success"
              size="sm"
              class="mr-1"
              :disabled="isUpdating"
              @click="saveProfile"
            >
              <b-spinner
                v-if="isUpdating"
                small
                class="mr-50"
              />
              <feather-icon
                v-else
                icon="SaveIcon"
                size="14"
                class="mr-50"
              />
              حفظ
            </b-button>
            <b-button
              variant="outline-secondary"
              size="sm"
              :disabled="isUpdating"
              @click="cancelEditing"
            >
              إلغاء
            </b-button>
          </div>
        </div>
      </b-card-title>

      <validation-observer ref="profileForm">
        <b-form @submit.prevent="saveProfile">
          <b-row>
            <!-- Name -->
            <b-col
              cols="12"
              md="6"
            >
              <b-form-group
                label="الاسم الكامل"
                label-for="name"
              >
                <validation-provider
                  #default="{ errors }"
                  name="الاسم"
                  rules="max:255"
                >
                  <b-form-input
                    id="name"
                    v-model="form.name"
                    :disabled="!isEditing"
                    :state="errors.length > 0 ? false : null"
                    placeholder="أدخل اسمك الكامل"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <!-- Email -->
            <b-col
              cols="12"
              md="6"
            >
              <b-form-group
                label="البريد الإلكتروني"
                label-for="email"
              >
                <validation-provider
                  #default="{ errors }"
                  name="البريد الإلكتروني"
                  rules="email|max:255"
                >
                  <b-form-input
                    id="email"
                    v-model="form.email"
                    type="email"
                    :disabled="!isEditing"
                    :state="errors.length > 0 ? false : null"
                    placeholder="أدخل بريدك الإلكتروني"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <!-- Phone -->
            <b-col
              cols="12"
              md="6"
            >
              <b-form-group
                label="رقم الهاتف"
                label-for="phone"
              >
                <b-form-input
                  id="phone"
                  v-model="form.phone"
                  :disabled="!isEditing"
                  placeholder="أدخل رقم هاتفك"
                />
              </b-form-group>
            </b-col>

            <!-- National ID -->
            <b-col
              cols="12"
              md="6"
            >
              <b-form-group
                label="الرقم القومي"
                label-for="national_id"
              >
                <b-form-input
                  id="national_id"
                  v-model="form.national_id"
                  :disabled="!isEditing"
                  placeholder="أدخل رقمك القومي"
                />
              </b-form-group>
            </b-col>

            <!-- Job Title -->
            <b-col
              cols="12"
              md="6"
            >
              <b-form-group
                label="المسمى الوظيفي"
                label-for="job_title"
              >
                <b-form-input
                  id="job_title"
                  v-model="form.job_title"
                  :disabled="!isEditing"
                  placeholder="أدخل مسماك الوظيفي"
                />
              </b-form-group>
            </b-col>

            <!-- Department -->
            <b-col
              cols="12"
              md="6"
            >
              <b-form-group
                label="القسم"
                label-for="department"
              >
                <b-form-input
                  id="department"
                  v-model="form.department"
                  :disabled="!isEditing"
                  placeholder="أدخل قسمك"
                />
              </b-form-group>
            </b-col>

            <!-- Hire Date -->
            <b-col
              cols="12"
              md="6"
            >
              <b-form-group
                label="تاريخ التعيين"
                label-for="hire_date"
              >
                <b-form-input
                  id="hire_date"
                  v-model="form.hire_date"
                  type="date"
                  :disabled="!isEditing"
                />
              </b-form-group>
            </b-col>

            <!-- Salary -->
            <b-col
              cols="12"
              md="6"
            >
              <b-form-group
                label="الراتب"
                label-for="salary"
              >
                <validation-provider
                  #default="{ errors }"
                  name="الراتب"
                  rules="min_value:0"
                >
                  <b-form-input
                    id="salary"
                    v-model="form.salary"
                    type="number"
                    step="0.01"
                    :disabled="!isEditing"
                    :state="errors.length > 0 ? false : null"
                    placeholder="أدخل راتبك"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <!-- Address -->
            <b-col cols="12">
              <b-form-group
                label="العنوان"
                label-for="address"
              >
                <b-form-textarea
                  id="address"
                  v-model="form.address"
                  rows="3"
                  :disabled="!isEditing"
                  placeholder="أدخل عنوانك"
                />
              </b-form-group>
            </b-col>

            <!-- Roles (Read-only) -->
            <b-col cols="12">
              <b-form-group label="الأدوار">
                <div>
                  <b-badge
                    v-for="role in userRoles"
                    :key="role.id"
                    variant="light-primary"
                    class="mr-50 mb-50"
                  >
                    {{ role.name }}
                  </b-badge>
                  <span
                    v-if="!hasRoles"
                    class="text-muted"
                  >
                    لا توجد أدوار مسندة
                  </span>
                </div>
              </b-form-group>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardTitle,
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BRow,
  BCol,
  BBadge,
  BSpinner,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, email } from '@validations'
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'ProfileInfo',
  components: {
    BCard,
    BCardTitle,
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BRow,
    BCol,
    BBadge,
    BSpinner,
    ValidationProvider,
    ValidationObserver,
  },
  data() {
    return {
      isEditing: false,
      form: {
        name: null,
        email: null,
        phone: null,
        national_id: null,
        job_title: null,
        department: null,
        hire_date: null,
        salary: null,
        address: null,
      },
      required,
      email,
    }
  },
  computed: {
    ...mapGetters('profile', {
      profile: 'profile',
      isUpdating: 'isUpdating',
      updateError: 'updateError',
    }),
    userRoles() {
      return this.profile && this.profile.roles ? this.profile.roles : []
    },
    hasRoles() {
      return this.userRoles && this.userRoles.length > 0
    },
  },
  watch: {
    profile: {
      immediate: true,
      handler(newProfile) {
        if (newProfile && !this.isEditing) {
          this.loadProfileData()
        }
      },
    },
  },
  methods: {
    ...mapActions('profile', {
      updateProfile: 'updateProfile',
    }),

    loadProfileData() {
      if (this.profile) {
        this.form = {
          name: this.profile.name,
          email: this.profile.email,
          phone: this.profile.phone,
          national_id: this.profile.national_id,
          job_title: this.profile.job_title,
          department: this.profile.department,
          hire_date: this.profile.hire_date,
          salary: this.profile.salary,
          address: this.profile.address,
        }
      }
    },

    startEditing() {
      this.isEditing = true
    },

    cancelEditing() {
      this.isEditing = false
      this.loadProfileData()
    },

    async saveProfile() {
      const isValid = await this.$refs.profileForm.validate()
      if (!isValid) {
        return
      }

      try {
        // Only send fields that have been modified
        const dataToUpdate = {}
        Object.keys(this.form).forEach(key => {
          if (this.form[key] !== this.profile[key]) {
            dataToUpdate[key] = this.form[key]
          }
        })

        if (Object.keys(dataToUpdate).length === 0) {
          this.$toast({
            component: this.$root.$refs.notificationToast,
            props: {
              title: 'معلومة',
              icon: 'InfoIcon',
              variant: 'info',
              text: 'لا توجد تغييرات للحفظ',
            },
          })
          this.isEditing = false
          return
        }

        const response = await this.updateProfile(dataToUpdate)

        this.$toast({
          component: this.$root.$refs.notificationToast,
          props: {
            title: 'نجاح',
            icon: 'CheckIcon',
            variant: 'success',
            text: response.message || 'تم تحديث الملف الشخصي بنجاح',
          },
        })

        this.isEditing = false
      } catch (error) {
        const errorMessage = error.response?.data?.message || 'فشل تحديث الملف الشخصي'
        this.$toast({
          component: this.$root.$refs.notificationToast,
          props: {
            title: 'خطأ',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: errorMessage,
          },
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.b-form-input:disabled,
.b-form-textarea:disabled {
  background-color: #f8f8f8;
  cursor: not-allowed;
}
</style>
